import { useCallback, useMemo, useState, useRef } from 'react';
import dayjs from 'dayjs';
import { useMyAchievements } from '../../../../lib/api/swrApi';
import FiltersViewContext, { FiltersInitialViewContextType } from '../../../../lib/context/FiltersViewContext';
import { FacetType, FilterData, SortByRadio } from '../../../../lib/utils/search/searchModel';
import FilteredCardsView from '../../base/FilteredCardsView/FilteredCardsView';
import { CommonFilterViewProps } from '../../base/FilteredCardsView/FilteredCardsView.types';
import { sortBy } from './AchievementsFacets';
import { API_DATE_FORMAT } from '../../../../lib/constants/globals';
import { isNil } from '../../../../lib/utils/isNil';
import { Api } from '../../../../lib/types/apiSpec';
import useErrorMapping from '../../../../lib/hooks/useErrorMapping';

const getInitialFilterData = (props: CommonFilterViewProps): FilterData<typeof FacetType.Radio> => {
  const today = dayjs();

  return {
    ...props,
    facets: [
      {
        name: Api.FacetName.completionDate,
        type: FacetType.Radio,
        values: [
          { label: 'last-30-days', from: today.subtract(30, 'days'), to: today },
          { label: 'last-60-days', from: today.subtract(60, 'days'), to: today },
          { label: 'dateRange', from: null, to: null, isDatePicker: true },
        ],
      },
    ],
    totalCount: 0,
    sortBy: SortByRadio.dateCompleted,
    sortDirection: Api.SortDirection.desc,
  };
};

const Achievements = ({ pageSize, ...rest }: CommonFilterViewProps) => {
  const previousCall = useRef<AbortController | undefined>(undefined);
  const [filtersState, setFiltersState] = useState<FilterData<typeof FacetType.Radio>>(
    getInitialFilterData({ pageSize, ...rest }),
  );
  const { getFilteredErrorMessage } = useErrorMapping();

  const { startDate, endDate } = useMemo(() => {
    const selectedDateItem = filtersState?.facets
      .find(item => item.name === Api.FacetName.completionDate)
      ?.values.find(item => item.selected);
    if (selectedDateItem) {
      return {
        startDate: selectedDateItem.from,
        endDate: selectedDateItem.to,
      };
    }

    return { startDate: null, endDate: null };
  }, [filtersState]);

  const getSignal = () => {
    if (previousCall.current) {
      previousCall.current.abort();
    }
    previousCall.current = new AbortController();

    return previousCall.current.signal;
  };

  const updateFilters = useCallback((newFilterData: FilterData<typeof FacetType.Radio>, page?: number) => {
    setFiltersState({ ...newFilterData, page });
  }, []);

  const clearFilters = useCallback(() => {
    setFiltersState(getInitialFilterData({ pageSize, ...rest }));
  }, []);

  const { data, isValidating, error } = useMyAchievements(
    getSignal,
    startDate?.format(API_DATE_FORMAT),
    endDate?.format(API_DATE_FORMAT),
  );

  const sorted = useMemo(() => sortBy(filtersState)(data ?? []), [filtersState, data]);

  const paginate = (arr: Api.AchievementBase[]) =>
    arr?.slice(pageSize * ((filtersState.page ?? 1) - 1), pageSize * (filtersState.page ?? 1)) ?? [];

  const achievementsToShow = paginate(sorted);

  const loading: FiltersInitialViewContextType['loading'] = (() => {
    if (isValidating && isNil(data)) {
      return 'initial';
    }
    if (isValidating && !isNil(data)) {
      return 'refresh';
    }

    return null;
  })();

  const value: FiltersInitialViewContextType = useMemo(
    () => ({
      ...rest,
      pageSize,
      from: ((filtersState.page ?? 1) - 1) * pageSize + 1,
      to: Math.min(((filtersState.page ?? 1) - 1) * pageSize + pageSize, sorted?.length ?? 0),
      filters: { ...filtersState, totalCount: sorted?.length ?? 0 },
      updateFilters,
      onClear: clearFilters,
      achievements: achievementsToShow,
      withSearch: false,
      sortByOptions: [SortByRadio.dateCompleted, SortByRadio.name],
      visibleFacetOptionsNumber: 3,
      loading,
      isAchievements: true,
      fetchingError: getFilteredErrorMessage(error),
    }),
    [filtersState, achievementsToShow, error],
  );

  return (
    <FiltersViewContext value={value}>
      <FilteredCardsView />
    </FiltersViewContext>
  );
};

export default Achievements;
